import { useState, useEffect } from "react";
import {
  Container,
  Stack,
  Card,
  Text,
  Group,
  Button,
  Badge,
  Table,
  ScrollArea,
  Alert,
  Modal,
  TextInput,
  Select,
  Textarea,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  IconBrandGithub,
  IconAlertCircle,
  IconKey,
  IconCheck,
  IconX,
  IconLoader,
} from "@tabler/icons-react";
import { PageHeader } from "../components/PageHeader";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { showSuccess } from "../lib/notifications";
import { useAuth } from "../contexts/AuthContext";
import { githubAccessApi } from "../lib/api";
import { GitHubAccessRequest } from "../types";

export function GitHubAccessPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [githubUsernameModalOpen, setGithubUsernameModalOpen] = useState(false);
  const [githubAccessModalOpen, setGithubAccessModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [usernameValidation, setUsernameValidation] = useState<{
    isValidating: boolean;
    isValid: boolean | null;
    message: string;
  }>({
    isValidating: false,
    isValid: null,
    message: "",
  });

  const [repositoryValidation, setRepositoryValidation] = useState<{
    isValidating: boolean;
    isValid: boolean | null;
    message: string;
  }>({
    isValidating: false,
    isValid: null,
    message: "",
  });

  const isAdmin = ["SUPERUSER", "ADMIN"].includes(user?.role || "");

  const githubUsernameForm = useForm({
    initialValues: {
      githubUsername: "",
    },
    validate: {
      githubUsername: (value: string) => {
        if (!value.trim()) return "GitHub username is required";
        return null;
      },
    },
  });

  const githubAccessForm = useForm({
    initialValues: {
      repository: "",
      accessType: "READ" as "READ" | "WRITE",
      reason: "",
    },
    validate: {
      repository: (value: string) => {
        if (!value.trim()) return "Repository is required";
        if (repositoryValidation.isValid === false)
          return repositoryValidation.message;
        return null;
      },
      accessType: (value: string) =>
        !value ? "Access type is required" : null,
    },
  });

  // GitHub access queries and mutations
  const { data: githubUsernameStatus } = useQuery({
    queryKey: ["githubUsernameStatus"],
    queryFn: async () => {
      try {
        const response = await githubAccessApi.getUsernameStatus();
        return response.data;
      } catch (error) {
        throw error;
      }
    },
  });

  const { data: githubAccessRequests, isLoading: isLoadingGitHubRequests } =
    useQuery<GitHubAccessRequest[]>({
      queryKey: ["githubAccessRequests"],
      queryFn: async () => {
        try {
          const response = await githubAccessApi.listRequests();
          return response.data;
        } catch (error) {
          throw error;
        }
      },
    });

  const validateUsername = useMutation({
    mutationFn: async (githubUsername: string) => {
      const response = await githubAccessApi.validateUsername(githubUsername);
      return response.data;
    },
    onSuccess: (response) => {
      setUsernameValidation({
        isValidating: false,
        isValid: response.valid,
        message: response.valid
          ? "Username is valid"
          : response.message || "Invalid username",
      });
    },
    onError: (error: any) => {
      setUsernameValidation({
        isValidating: false,
        isValid: false,
        message: error.response?.data?.message || "Failed to validate username",
      });
    },
  });

  const validateRepository = useMutation({
    mutationFn: async (repository: string) => {
      const response = await githubAccessApi.validateRepository(repository);
      return response.data;
    },
    onSuccess: (response) => {
      setRepositoryValidation({
        isValidating: false,
        isValid: response.valid,
        message: response.valid
          ? "Repository is valid"
          : response.message || "Invalid repository",
      });
    },
    onError: (error: any) => {
      setRepositoryValidation({
        isValidating: false,
        isValid: false,
        message:
          error.response?.data?.message || "Failed to validate repository",
      });
    },
  });

  // Manual validation function
  const handleValidateUsername = () => {
    const username = githubUsernameForm.values.githubUsername.trim();
    if (!username) {
      setUsernameValidation({
        isValidating: false,
        isValid: false,
        message: "Please enter a GitHub username",
      });
      return;
    }
    validateUsername.mutate(username);
  };

  const handleValidateRepository = () => {
    const repository = githubAccessForm.values.repository.trim();
    if (!repository) {
      setRepositoryValidation({
        isValidating: false,
        isValid: false,
        message: "Please enter a repository name",
      });
      return;
    }
    validateRepository.mutate(repository);
  };

  // Reset validation when username changes after validation
  useEffect(() => {
    if (usernameValidation.isValid !== null) {
      setUsernameValidation({
        isValidating: false,
        isValid: null,
        message: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [githubUsernameForm.values.githubUsername]);

  // Reset validation when repository changes after validation
  useEffect(() => {
    if (repositoryValidation.isValid !== null) {
      setRepositoryValidation({
        isValidating: false,
        isValid: null,
        message: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [githubAccessForm.values.repository]);

  const updateGitHubUsername = useMutation({
    mutationFn: async (githubUsername: string) => {
      const response = await githubAccessApi.updateUsername(githubUsername);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["githubUsernameStatus"]);
      setGithubUsernameModalOpen(false);
      githubUsernameForm.reset();
      setUsernameValidation({
        isValidating: false,
        isValid: null,
        message: "",
      });
      showSuccess("GitHub username updated successfully");
    },
    onError: (error: any) => {
      setError(
        error.response?.data?.error || "Failed to update GitHub username"
      );
    },
  });

  const requestGitHubAccess = useMutation({
    mutationFn: async (values: {
      repository: string;
      accessType: "READ" | "WRITE";
      reason?: string;
    }) => {
      const response = await githubAccessApi.requestAccess(
        values.repository,
        values.accessType,
        values.reason
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["githubAccessRequests"]);
      setGithubAccessModalOpen(false);
      githubAccessForm.reset();
      showSuccess("GitHub access request sent successfully");
    },
    onError: (error: any) => {
      setError(
        error.response?.data?.error || "Failed to request GitHub access"
      );
    },
  });

  const respondToGitHubRequest = useMutation({
    mutationFn: async ({
      requestId,
      status,
    }: {
      requestId: string;
      status: "APPROVED" | "REJECTED";
    }) => {
      const response = await githubAccessApi.respondToRequest(
        requestId,
        status
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["githubAccessRequests"]);
      showSuccess("GitHub access request updated successfully");
    },
    onError: (error: any) => {
      setError(
        error.response?.data?.error || "Failed to update GitHub access request"
      );
    },
  });

  if (isLoadingGitHubRequests) {
    return <LoadingOverlay message="Loading..." />;
  }

  return (
    <Container fluid p={0}>
      <PageHeader
        title="GitHub Access"
        description="Manage your GitHub repository access requests"
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          mb="md"
          title="Error"
          onClose={() => setError("")}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      <Stack spacing="md">
        {/* GitHub Username Section */}
        <Card shadow="sm" p="lg" radius="md" withBorder>
          <Group position="apart" mb="md">
            <div>
              <Text weight={500} size="lg">
                GitHub Username
              </Text>
              <Text size="sm" color="dimmed">
                Set your GitHub username to request repository access
              </Text>
            </div>
            {githubUsernameStatus?.canSetUsername && (
              <Button
                leftIcon={<IconBrandGithub size={16} />}
                onClick={() => setGithubUsernameModalOpen(true)}
              >
                Set Username
              </Button>
            )}
          </Group>

          {githubUsernameStatus?.hasUsername ? (
            <Group>
              <Text>Current username:</Text>
              <Badge color="green" leftSection={<IconBrandGithub size={14} />}>
                {githubUsernameStatus.username}
              </Badge>
              <Text size="sm" color="dimmed">
                (Cannot be changed once set)
              </Text>
            </Group>
          ) : (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              You need to set your GitHub username before requesting repository
              access.
            </Alert>
          )}
        </Card>

        {/* GitHub Access Requests Section */}
        <Card shadow="sm" p="lg" radius="md" withBorder>
          <Group position="apart" mb="md">
            <div>
              <Text weight={500} size="lg">
                Repository Access Requests
              </Text>
              <Text size="sm" color="dimmed">
                Request access to Small Group repositories
              </Text>
            </div>
            {githubUsernameStatus?.hasUsername && (
              <Button
                leftIcon={<IconKey size={16} />}
                onClick={() => setGithubAccessModalOpen(true)}
              >
                Request Access
              </Button>
            )}
          </Group>

          {githubAccessRequests && githubAccessRequests.length > 0 ? (
            <ScrollArea>
              <Table>
                <thead>
                  <tr>
                    <th>Repository</th>
                    <th>Access Type</th>
                    <th>Reason</th>
                    <th>Requested At</th>
                    <th>Status</th>
                    {isAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {githubAccessRequests.map((request: GitHubAccessRequest) => (
                    <tr key={request.id}>
                      <td>
                        <Text weight={500}>{request.repository}</Text>
                      </td>
                      <td>
                        <Badge
                          color={request.accessType === "WRITE" ? "red" : "blue"}
                        >
                          {request.accessType}
                        </Badge>
                      </td>
                      <td>
                        <Text size="sm">
                          {request.reason || "No reason provided"}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm" color="dimmed">
                          {new Date(request.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </Text>
                      </td>
                      <td>
                        <Stack spacing="xs">
                          <Badge
                            color={
                              request.status === "PENDING"
                                ? "yellow"
                                : request.status === "APPROVED"
                                ? "green"
                                : "red"
                            }
                          >
                            {request.status}
                          </Badge>
                          {request.status === "APPROVED" &&
                            request.githubStatus && (
                              <Tooltip
                                label={
                                  request.githubStatus.error ||
                                  "Successfully granted GitHub access"
                                }
                                disabled={request.githubStatus.success}
                              >
                                <Badge
                                  size="sm"
                                  color={
                                    request.githubStatus.success
                                      ? "green"
                                      : "orange"
                                  }
                                  variant="light"
                                >
                                  {request.githubStatus.success
                                    ? "GitHub Access Granted"
                                    : "GitHub API Error"}
                                </Badge>
                              </Tooltip>
                            )}
                        </Stack>
                      </td>
                      {isAdmin && request.status === "PENDING" && (
                        <td>
                          <Group spacing={4}>
                            <Tooltip label="Approve">
                              <ActionIcon
                                color="green"
                                onClick={() =>
                                  respondToGitHubRequest.mutate({
                                    requestId: request.id,
                                    status: "APPROVED",
                                  })
                                }
                                loading={respondToGitHubRequest.isLoading}
                              >
                                <IconCheck size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Reject">
                              <ActionIcon
                                color="red"
                                onClick={() =>
                                  respondToGitHubRequest.mutate({
                                    requestId: request.id,
                                    status: "REJECTED",
                                  })
                                }
                                loading={respondToGitHubRequest.isLoading}
                              >
                                <IconX size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          ) : (
            <Text color="dimmed" align="center" py="xl">
              {isAdmin
                ? "No pending GitHub access requests"
                : "No GitHub access requests found"}
            </Text>
          )}
        </Card>
      </Stack>

      {/* GitHub Username Modal */}
      <Modal
        opened={githubUsernameModalOpen}
        size="lg"
        onClose={() => {
          setGithubUsernameModalOpen(false);
          setError("");
          githubUsernameForm.reset();
          setUsernameValidation({
            isValidating: false,
            isValid: null,
            message: "",
          });
        }}
        title="Set GitHub Username"
      >
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            mb="md"
            title="Error"
            onClose={() => setError("")}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        <form
          onSubmit={githubUsernameForm.onSubmit((values) =>
            updateGitHubUsername.mutate(values.githubUsername)
          )}
        >
          <Stack>
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">
                <strong>Important:</strong> Your GitHub username can only be set
                once and cannot be changed later. Please verify your username
                before submitting.
              </Text>
            </Alert>

            <TextInput
              label="GitHub Username"
              placeholder="Enter your GitHub username"
              description="This will be used to identify you for repository access requests"
              value={githubUsernameForm.values.githubUsername}
              onChange={(event) =>
                githubUsernameForm.setFieldValue(
                  "githubUsername",
                  event.currentTarget.value
                )
              }
              error={
                usernameValidation.isValid === false
                  ? usernameValidation.message
                  : githubUsernameForm.errors.githubUsername
              }
              rightSection={
                validateUsername.isLoading ? (
                  <IconLoader size={16} className="animate-spin" />
                ) : usernameValidation.isValid === true ? (
                  <IconCheck size={16} color="green" />
                ) : usernameValidation.isValid === false ? (
                  <IconX size={16} color="red" />
                ) : null
              }
            />

            {usernameValidation.isValid ? (
              <Button
                type="submit"
                loading={updateGitHubUsername.isLoading}
                disabled={!githubUsernameForm.values.githubUsername.trim()}
                color="green"
                fullWidth
              >
                Save
              </Button>
            ) : (
              <Button
                variant="filled"
                onClick={handleValidateUsername}
                loading={validateUsername.isLoading}
                disabled={!githubUsernameForm.values.githubUsername.trim()}
                fullWidth
              >
                Verify
              </Button>
            )}
          </Stack>
        </form>
      </Modal>

      {/* GitHub Access Request Modal */}
      <Modal
        size="lg"
        opened={githubAccessModalOpen}
        onClose={() => {
          setGithubAccessModalOpen(false);
          setError("");
          githubAccessForm.reset();
        }}
        title="Request GitHub Repository Access"
      >
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            mb="md"
            title="Error"
            onClose={() => setError("")}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        <form
          onSubmit={githubAccessForm.onSubmit((values) =>
            requestGitHubAccess.mutate(values)
          )}
        >
          <Stack>
            <TextInput
              label="Repository"
              placeholder="repository-name"
              description="Enter the repository name from the Small-Group organization"
              value={githubAccessForm.values.repository}
              onChange={(event) =>
                githubAccessForm.setFieldValue(
                  "repository",
                  event.currentTarget.value
                )
              }
              error={
                repositoryValidation.isValid === false
                  ? repositoryValidation.message
                  : githubAccessForm.errors.repository
              }
              rightSection={
                validateRepository.isLoading ? (
                  <IconLoader size={16} className="animate-spin" />
                ) : repositoryValidation.isValid === true ? (
                  <IconCheck size={16} color="green" />
                ) : repositoryValidation.isValid === false ? (
                  <IconX size={16} color="red" />
                ) : null
              }
            />

            <Select
              label="Access Type"
              description="Select the type of access you need"
              data={[
                { value: "READ", label: "Read Access" },
                { value: "WRITE", label: "Write Access" },
              ]}
              {...githubAccessForm.getInputProps("accessType")}
            />

            <Textarea
              label="Reason"
              description="Explain why you need access to this repository"
              placeholder="I need this access to..."
              {...githubAccessForm.getInputProps("reason")}
            />

            <Text size="sm" color="dimmed">
              An admin will review your request and grant you appropriate
              access.
            </Text>

            {repositoryValidation.isValid ? (
              <Button
                type="submit"
                loading={requestGitHubAccess.isLoading}
                disabled={!githubAccessForm.values.repository.trim()}
                color="green"
                fullWidth
              >
                Submit Request
              </Button>
            ) : (
              <Button
                variant="filled"
                onClick={handleValidateRepository}
                loading={validateRepository.isLoading}
                disabled={!githubAccessForm.values.repository.trim()}
                fullWidth
              >
                Validate request
              </Button>
            )}
          </Stack>
        </form>
      </Modal>
    </Container>
  );
}
